"use client";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import Image from "next/image";
import { Trigger } from "@radix-ui/react-dropdown-menu";
import { KeyboardArrowDown } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdownMenu";
import { ElementType } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import EventLink from "@/components/Link/EventLink/EventLink";
import { getEnv } from "@/env";

interface UserMenuProps {
  headerUrls?: {
    logout: string;
    accountSettings: string;
    learnerProfile: string;
    resumeBlock: string;
  };
  userImage?: string;
}

function UserMenuTrigger({ children }: { children: React.ReactNode }) {
  const webElement: ElementViewed["webElement"] = {
    elementType: ElementType.Button,
    htmlId: "logged-in-profile-button",
    location: "nav-bar",
    position: "1",
    name: "logged-in-profile-button",
  };

  const { elementViewed, elementClicked } = useElementEvents({ webElement });
  const { inView, ref } = useInView();

  useEffect(() => {
    if (inView) {
      elementViewed();
    }
  }, [inView, elementViewed]);

  const handleClick = () => {
    elementClicked();
  };

  return (
    <Trigger asChild>
      <Button
        variant="ghost"
        className="text-medium"
        ref={ref}
        onClick={handleClick}
      >
        {children}
      </Button>
    </Trigger>
  );
}

const itemClass =
  "not-prose inline-block w-full text-base text-primary py-2 px-4 hover:bg-putty-200";

function UserMenuItem({
  href,
  label,
  isDashboard = false,
}: {
  href: string;
  label: string;
  isDashboard?: boolean;
}) {
  return (
    <DropdownMenuItem asChild>
      {isDashboard ? (
        <EventLink
          href={href}
          text={label}
          html_id="logged-in-profile-button"
          location="nav-bar"
          name="dashboard"
          element_type={ElementType.Button}
          class_name={itemClass}
        />
      ) : (
        <a href={href} className={itemClass}>
          {label}
        </a>
      )}
    </DropdownMenuItem>
  );
}

export default function UserMenu({ headerUrls, userImage }: UserMenuProps) {
  const lmsBase = getEnv("NEXT_PUBLIC_LMS_BASE_URL");
  const careersUrl = getEnv("NEXT_PUBLIC_CAREERS_BASE_URL");
  const homeUrl = getEnv("NEXT_PUBLIC_HOME_BASE_URL");

  return (
    <DropdownMenu>
      <UserMenuTrigger>
        <Image
          src={userImage || ""}
          alt="Account menu"
          height={36}
          width={36}
          className="rounded-full mr-2"
        />
        <KeyboardArrowDown svgProps={{ className: "flex-none" }} />
      </UserMenuTrigger>
      <DropdownMenuContent className="w-80">
        {headerUrls?.resumeBlock && (
          <UserMenuItem
            href={headerUrls.resumeBlock}
            label="Resume your last course"
          />
        )}
        <UserMenuItem href={homeUrl} label="Dashboard" isDashboard />
        <UserMenuItem href={careersUrl} label="Career" />
        {headerUrls?.learnerProfile && (
          <UserMenuItem href={headerUrls.learnerProfile} label="Profile" />
        )}
        {headerUrls?.accountSettings && (
          <UserMenuItem href={headerUrls.accountSettings} label="Account" />
        )}
        <UserMenuItem href={`${lmsBase}/logout`} label="Sign Out" />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
